'use client';

import React, { ButtonHTMLAttributes, forwardRef } from 'react';

import LoadingDots from '@/components/ui/LoadingDots';

import styles from './Button.module.css';
import cn from 'classnames';

// Import Google Analytics library
declare global {
  interface Window {
    gtag: any;
  }
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'outline' | 'slim' | 'flat' | 'navbar';
  active?: boolean;
  width?: number;
  loading?: boolean;
  Component?: React.ElementType;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    className,
    variant = 'primary',
    children,
    active,
    width,
    loading = false,
    disabled = false,
    style = {},
    Component = 'button',
    onClick,
    ...rest
  } = props;

  const rootClassName = cn(
    styles.root,
    {
      [styles.primary]: variant === 'primary',
      [styles.outline]: variant === 'outline',
      [styles.slim]: variant === 'slim',
      [styles.flat]: variant === 'flat',
      [styles.navbar]: variant === 'navbar',
      [styles.loading]: loading,
      [styles.active]: active
    },
    className
  );

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (window.gtag) {
      window.gtag('event', 'button_click', {
        event_category: 'Button',
        event_label: typeof children === 'string' ? children : 'Button Clicked'
      });
    }
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <Component
      aria-pressed={active}
      data-variant={variant}
      ref={ref}
      className={rootClassName}
      disabled={disabled || loading}
      style={{
        width,
        ...style
      }}
      onClick={handleClick}
      {...rest}>
      {children}
      {loading && (
        <i className='m-0 flex pl-2'>
          <LoadingDots />
        </i>
      )}
    </Component>
  );
});

Button.displayName = 'Button';

export default Button;
